'use client';

import { VehicleSearchForm } from '@/components/app-home-page/vehicle-search-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { WarrantyPilotPost } from '~/dtos/blog';

export function BlogListingComponent({
  initialPosts = [],
  initialCategories = ['All'],
}: {
  initialPosts?: WarrantyPilotPost[];
  initialCategories?: string[];
}) {
  // Rendered entirely from server-provided props. If the server-side WordPress fetch
  // failed (upstream down), initialPosts is empty and the listing simply shows no cards
  // — the hero + category bar + search form still render. The category filter is the
  // only client-side state.
  const [selectedCategory, setSelectedCategory] = useState('All');

  const blogPosts =
    selectedCategory === 'All'
      ? initialPosts
      : initialPosts.filter((post) => post.categories.some((category) => category.text === selectedCategory));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-[#00B67A] text-white pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">WarrantyPilot Blog</h1>
            <p className="text-xl md:text-2xl">
              Expert advice and insights to help you make informed decisions about vehicle protection
            </p>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Categories */}
            <div className="mb-12 flex flex-wrap justify-center gap-3">
              {initialCategories.map((category) => (
                <Button
                  key={`btn${category}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    category === selectedCategory
                      ? 'bg-[#00B67A] text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>

            {/* Blog Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link key={`link-${post.postId}`} href={`/blog/${post.slug}`}>
                  <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow duration-300 border-none !border-2 !border-[#00B67A]">
                    <div className="relative h-48 w-full overflow-hidden">
                      <Image
                        src={post.featuredImage}
                        alt={post.title}
                        width={600}
                        height={400}
                        className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
                      />
                      <div className="absolute top-4 left-4">
                        {post.categories.map((category) => (
                          <span
                            key={`${category.id}-${category.text}`}
                            className="inline-block px-3 py-1 bg-white/90 text-[#00B67A] text-xs font-medium rounded-full"
                          >
                            {category.text}
                          </span>
                        ))}
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center mb-4">
                        <div className="h-10 w-10 rounded-full overflow-hidden mr-3">
                          <Image
                            src={post.author.avatar}
                            alt={post.author.name}
                            width={40}
                            height={40}
                            className="object-cover"
                          />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{post.author.name}</span>
                          <div className="flex items-center text-xs text-gray-500">
                            <span>{post.postDate}</span>
                            <span className="mx-2">•</span>
                            <span>{post.estimatedReadingTime}</span>
                          </div>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold mb-2 line-clamp-2">{post.title}</h3>
                      {/* div, not p: WordPress content contains block tags that can't nest in a <p> */}
                      <div
                        className="text-gray-600 line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                      ></div>
                      <div className="mt-4 flex items-center text-[#00B67A] font-medium">
                        Read more
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="pt-8">
              <CardContent>
                <h2 className="text-2xl font-semibold mb-6 text-center">Search Your Vehicle</h2>
                <VehicleSearchForm />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
